import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Loader2, ScanLine, RotateCcw, CheckCircle2, Circle, AlertTriangle, Camera, X, Target, Droplet, ShieldAlert, Ban, Info, Sparkles, BookmarkPlus, Check, Crown, UserPlus } from "lucide-react";
import { GradeBadge, WadaBadge } from "@/components/Badges";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const GUEST_FLAG = "suppev_guest_scan_used";

const guestSession = () => {
  try {
    let s = localStorage.getItem("suppev_guest_session");
    if (!s) {
      s = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(16).slice(2);
      localStorage.setItem("suppev_guest_session", s);
    }
    return s;
  } catch { return ""; }
};

const guestFingerprint = async () => {
  try {
    const parts = [
      navigator.userAgent, navigator.language, (navigator.languages || []).join(","),
      navigator.platform, `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
      new Date().getTimezoneOffset(), navigator.hardwareConcurrency || 0, navigator.deviceMemory || 0,
    ].join("|");
    if (crypto?.subtle) {
      const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(parts));
      return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
    }
  } catch { /* fall through */ }
  // non-crypto fallback hash
  let h = 0, str = (navigator.userAgent || "") + (navigator.platform || "");
  for (let i = 0; i < str.length; i++) { h = (h << 5) - h + str.charCodeAt(i); h |= 0; }
  return "fp" + Math.abs(h).toString(16);
};

const SAMPLES = [
  { id: "creatine", label: "Creatine Monohydrate" },
  { id: "preworkout", label: "Pre-Workout NO Stack" },
  { id: "whey", label: "Whey Protein Isolate" },
];

const GOALS = ["strength", "endurance", "energy", "recovery", "weight_loss", "general_health", "heart_health"];
const LOAD_CLS = { low: "text-emerald-600 bg-emerald-50", moderate: "text-amber-600 bg-amber-50", high: "text-red-600 bg-red-50" };
const SEV_CLS = { info: "border-blue-200 bg-blue-50 text-blue-700", caution: "border-amber-200 bg-amber-50 text-amber-800", danger: "border-red-200 bg-red-50 text-red-700" };
const SEV_ICON = { info: Info, caution: ShieldAlert, danger: Ban };

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const stripPrefix = (dataUrl) => (dataUrl || "").split(",").pop();

export default function Scanner() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [goal, setGoal] = useState("general_health");
  const [scannedGoal, setScannedGoal] = useState("general_health");
  const [meds, setMeds] = useState("");
  const [saved, setSaved] = useState(false);
  const [usage, setUsage] = useState(null);
  const [showGuestCard, setShowGuestCard] = useState(false);

  const fetchUsage = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/usage");
      setUsage(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchUsage(); }, [user]);

  const limitReached = usage && usage.remaining <= 0;

  const saveToStack = async () => {
    try {
      await api.post("/stack", {
        product_name: result.product_name || "Supplement",
        ingredients: (result.ingredients || []).map((i) => ({ name: i.name, amount: i.amount })),
      });
      setSaved(true);
      toast.success(t("scan.savedToStack"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    setCameraOn(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setCameraOn(true);
      setResult(null);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } }, 50);
    } catch {
      toast.error(t("scan.cameraError"));
    }
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1080;
    canvas.height = video.videoHeight || 1080;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setPreview(dataUrl);
    setB64(stripPrefix(dataUrl));
    stopCamera();
  };

  const loadFile = async (file) => {
    if (!file) return;
    const dataUrl = await fileToBase64(file);
    setPreview(dataUrl);
    setB64(stripPrefix(dataUrl));
    setResult(null);
  };

  const loadSample = async (id) => {
    try {
      const res = await fetch(`/samples/${id}.jpg`);
      const blob = await res.blob();
      const dataUrl = await fileToBase64(new File([blob], `${id}.jpg`, { type: "image/jpeg" }));
      setPreview(dataUrl);
      setB64(stripPrefix(dataUrl));
      setResult(null);
    } catch {
      toast.error("Could not load sample");
    }
  };

  const analyze = async () => {
    if (!b64) return;
    // Guest gate: a logged-out visitor gets ONE free scan. A second scan requires an account.
    if (!user && localStorage.getItem(GUEST_FLAG) === "1") {
      setShowGuestCard(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setScanning(true); setResult(null); setScannedGoal(goal); setSaved(false);
    try {
      const config = {};
      if (!user) {
        const fp = await guestFingerprint();
        config.headers = { "X-Guest-Fingerprint": fp, "X-Guest-Session": guestSession() };
      }
      const { data } = await api.post("/scan", { image_base64: b64, language: lang, goal, medications: meds }, config);
      setResult(data);
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("trackCustom", "Scan");
      }
      if (!user) {
        localStorage.setItem(GUEST_FLAG, "1");
        setShowGuestCard(true);
      } else {
        fetchUsage();
      }
      toast.success(t("scan.complete"));
    } catch (e) {
      const status = e?.response?.status;
      if (!user && (status === 402 || status === 429)) {
        localStorage.setItem(GUEST_FLAG, "1");
        setShowGuestCard(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(apiError(e) || t("scan.error"));
      }
    } finally { setScanning(false); }
  };

  const reset = () => { setPreview(null); setB64(null); setResult(null); setSaved(false); };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("scan.title")}</h1>
        <p className="mt-1 text-sm text-gray-600">{t("scan.sub")}</p>
        {usage && (
          usage.is_pro ? (
            usage.remaining > 10 ? (
              <span data-testid="scan-usage-badge" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">
                <ScanLine size={13} /> {t("scan.unlimited")}
              </span>
            ) : (
              <span data-testid="scan-usage-badge" className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${limitReached ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
                <ScanLine size={13} /> {usage.remaining} {t("scan.scansLeftFair")}
              </span>
            )
          ) : (
            <span data-testid="scan-usage-badge" className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${limitReached ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}`}>
              <ScanLine size={13} /> {Math.max(0, usage.remaining)}/{usage.limit} {t("scan.freeScans")}
            </span>
          )
        )}
      </div>

      {!user && showGuestCard && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} data-testid="guest-account-card"
          className="mb-6 rounded-2xl border border-emerald-200 bg-white p-6 shadow-sm">
          <p className="text-sm leading-relaxed text-gray-700">
            {t("scan.guestUsed")}
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button onClick={() => navigate("/register")} data-testid="guest-create-account-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600">
              <UserPlus size={16} /> {t("scan.guestCreate")}
            </button>
            <button onClick={() => setShowGuestCard(false)} data-testid="guest-not-now-button"
              className="inline-flex items-center justify-center rounded-full border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50">
              {t("scan.guestNotNow")}
            </button>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">{t("scan.guestFree")}</p>
              <p className="mt-1 text-sm text-gray-600">{t("scan.guestFreeDesc")}</p>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-600">{t("scan.guestPaid")}</p>
              <p className="mt-1 text-sm text-gray-700">{t("scan.guestPaidDesc")}</p>
            </div>
          </div>
          <Link to="/pricing" data-testid="guest-see-plans-link" className="mt-4 inline-block text-sm font-bold text-emerald-600 hover:underline">
            {t("scan.guestSeePlans")}
          </Link>
        </motion.div>
      )}

      {limitReached && !result && (
        <div className="mb-6 flex flex-col items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="scan-limit-banner">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-800"><Crown size={16} className="text-amber-500" /> {usage.is_pro ? t("scan.fairUseReached") : t("scan.limitReached")}</p>
          {!usage.is_pro && <Link to="/pricing" data-testid="scan-upgrade-link" className="shrink-0 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-600">{t("scan.upgradeUnlimited")}</Link>}
        </div>
      )}

      {!result && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          {cameraOn && (
            <div className="relative mb-4 overflow-hidden rounded-xl bg-black" data-testid="scanner-camera-panel">
              <video ref={videoRef} playsInline muted className="h-72 w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 bg-gradient-to-t from-black/70 to-transparent p-4">
                <button onClick={capture} data-testid="scanner-capture-button"
                  className="flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-600">
                  <Camera size={16} /> {t("scan.capture")}
                </button>
                <button onClick={stopCamera} data-testid="scanner-close-camera-button"
                  className="flex items-center gap-1.5 rounded-full bg-white/90 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-white">
                  <X size={15} /> {t("scan.closeCamera")}
                </button>
              </div>
            </div>
          )}
          <div
            data-testid="scanner-dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); loadFile(e.dataTransfer.files?.[0]); }}
            className="relative flex min-h-[240px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center transition-colors hover:border-emerald-400 hover:bg-emerald-50/40"
          >
            {preview ? (
              <>
                <img src={preview} alt="label" className="max-h-64 rounded-lg object-contain" />
                {scanning && (
                  <motion.div
                    initial={{ top: 0 }} animate={{ top: ["0%", "100%", "0%"] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="pointer-events-none absolute left-0 right-0 h-0.5 bg-emerald-400 shadow-[0_0_12px_2px_rgba(16,185,129,0.8)]"
                  />
                )}
              </>
            ) : (
              <>
                <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
                  <Upload size={26} />
                </div>
                <p className="text-sm font-semibold text-gray-700">{t("scan.drop")}</p>
              </>
            )}
            <input ref={inputRef} data-testid="scanner-file-input" type="file" accept="image/*" className="hidden" onChange={(e) => loadFile(e.target.files?.[0])} />
          </div>

          <div className="mt-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">{t("scan.samples")}</p>
              <button onClick={startCamera} data-testid="scanner-camera-button"
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100">
                <Camera size={15} /> {t("scan.camera")}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {SAMPLES.map((s) => (
                <button key={s.id} data-testid={`scanner-sample-button-${s.id}`} onClick={() => loadSample(s.id)}
                  className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50">
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400"><Target size={13} /> {t("scan.goalLabel")}</p>
            <div className="flex flex-wrap gap-2">
              {GOALS.map((g) => (
                <button key={g} type="button" data-testid={`scanner-goal-${g}`} onClick={() => setGoal(g)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${goal === g ? "bg-emerald-500 text-white" : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}>
                  {t(`goal.${g}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400"><Droplet size={13} /> {t("scan.medsLabel")}</label>
            <input data-testid="scanner-meds-input" value={meds} onChange={(e) => setMeds(e.target.value)} placeholder={t("scan.medsPlaceholder")}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
          </div>

          <button data-testid="scanner-start-scan-button" onClick={analyze} disabled={!b64 || scanning || limitReached}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-50">
            {scanning ? <><Loader2 size={17} className="animate-spin" /> {t("scan.scanning")}</> : <><ScanLine size={17} /> {t("scan.start")}</>}
          </button>
        </div>
      )}

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="scanner-results-container">
            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400">{t("scan.results")}</p>
                  <h2 className="mt-1 text-xl font-bold text-gray-900">{result.product_name || "—"}</h2>
                </div>
                <div className="flex items-center gap-2">
                  {user && result.ingredients?.length > 0 && (
                    <button onClick={saveToStack} disabled={saved} data-testid="scanner-save-stack-button"
                      className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60">
                      {saved ? <><Check size={15} /> {t("scan.savedToStack")}</> : <><BookmarkPlus size={15} /> {t("scan.saveToStack")}</>}
                    </button>
                  )}
                  <button onClick={reset} data-testid="scanner-reset-button" className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                    <RotateCcw size={15} /> {t("scan.again")}
                  </button>
                </div>
              </div>

              <div className="mt-5 flex items-center gap-4 rounded-xl bg-emerald-50 p-4" data-testid="scanner-evidence-score-badge">
                <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-white">
                  <span className="text-xl font-extrabold text-emerald-600">{result.overall_evidence_score ?? "—"}</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-emerald-800">{t("scan.overall")}</p>
                  <p className="text-sm text-emerald-700">{result.summary}</p>
                </div>
              </div>

              {result.goal_fit && (
                <div className="mt-6 flex items-center gap-4 rounded-xl border border-emerald-100 bg-emerald-50/50 p-4" data-testid="scanner-goal-fit">
                  <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-full bg-emerald-500 text-white">
                    <span className="text-base font-extrabold leading-none">{result.goal_fit.score ?? "—"}</span>
                    <span className="text-[8px] font-semibold uppercase">/100</span>
                  </div>
                  <div>
                    <p className="flex items-center gap-1.5 text-sm font-bold text-emerald-800"><Target size={14} /> {t("scan.goalFitTitle")}: {t(`goal.${scannedGoal}`)}</p>
                    <p className="mt-0.5 text-sm text-emerald-700">{result.goal_fit.note}</p>
                  </div>
                </div>
              )}

              {result.clean_label && (
                <div className="mt-5 rounded-xl border border-gray-100 p-4" data-testid="scanner-clean-label">
                  <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-gray-800"><Sparkles size={15} className="text-emerald-500" /> {t("scan.cleanLabelTitle")}</p>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {[["fillers", result.clean_label.has_fillers], ["preservatives", result.clean_label.has_artificial_preservatives], ["additives", result.clean_label.has_artificial_additives]].map(([k, bad]) => (
                      <div key={k} className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs font-semibold ${bad ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                        <span>{t(`scan.${k}`)}</span>
                        <span>{bad ? t("scan.detected") : t("scan.clean")}</span>
                      </div>
                    ))}
                  </div>
                  {result.clean_label.flagged?.length > 0 && (
                    <p className="mt-3 text-sm text-gray-600"><span className="font-semibold">{t("scan.detected")}:</span> {result.clean_label.flagged.join(", ")}</p>
                  )}
                  {result.clean_label.note && <p className="mt-2 text-sm leading-relaxed text-gray-600">{result.clean_label.note}</p>}
                </div>
              )}

              {result.organ_load && (
                <div className="mt-5 rounded-xl border border-gray-100 p-4" data-testid="scanner-organ-load">
                  <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-gray-800"><Droplet size={15} className="text-emerald-500" /> {t("scan.organTitle")}</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {[["kidney", result.organ_load.kidney], ["liver", result.organ_load.liver]].map(([organ, lvl]) => (
                      <div key={organ} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold ${LOAD_CLS[lvl] || LOAD_CLS.low}`}>
                        <span>{t(`scan.${organ}`)}</span>
                        <span className="uppercase">{t(`scan.${lvl}`) || lvl}</span>
                      </div>
                    ))}
                  </div>
                  {result.organ_load.note && <p className="mt-2 text-sm leading-relaxed text-gray-600">{result.organ_load.note}</p>}
                  <p className="mt-2 text-xs leading-relaxed text-gray-400">{t("scan.organLegend")}</p>
                </div>
              )}

              {(() => {
                const danger = (result.interactions || []).filter((it) => it.severity !== "info");
                return (
              <div className="mt-5" data-testid="scanner-interactions">
                <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-gray-800"><ShieldAlert size={15} className="text-emerald-500" /> {t("scan.interactionsTitle")}</p>
                {danger.length > 0 ? (
                  <div className="space-y-2">
                    {danger.map((it, i) => {
                      const Ic = SEV_ICON[it.severity] || Info;
                      return (
                        <div key={i} className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${SEV_CLS[it.severity] || SEV_CLS.info}`}>
                          <Ic size={15} className="mt-0.5 shrink-0" />
                          <p><span className="font-bold">{it.with}:</span> {it.note}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-500">{t("scan.noInteractions")}</p>
                )}
              </div>
                );
              })()}

              <div className="mt-5 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-800" data-testid="scanner-safety-note">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {t("scan.safetyNote")}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
